<template>
  <div class="iframe-element" :class="wrapperClassList" :style="iframeStyling">
    <iframe
      v-if="model.state.settings?.url && model.state.settings.url !== ''"
      class="iframe-element__item"
      :class="{
        'iframe-element--show-scrollbars': model.state.settings.showScrollbars
      }"
      :src="model.state.settings?.url"
      :scrolling="model.state.settings?.showScrollbars ? 'auto' : 'no'"
      style="border: 0"
      allowtransparency="true"
    >
    </iframe>
  </div>
</template>

<script lang="ts">
import type { CSSProperties, PropType } from 'vue';
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue';
import type { AddonIframeElementModel } from '@/src/components/addons/iframe-element/Model';
import type { ClassList } from '@/src/typings/types/types';
import { IframeWidthType } from '@/src/typings/enums/enums';

export default defineComponent({
  name: 'AddonIframeElement',
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<AddonIframeElementModel>,
      required: true
    }
  },
  setup(props) {
    const iframeResponsiveHeight = ref<number | undefined>(undefined);

    const getDomain = (src: string): string | undefined => {
      const matches = src.match(/^https?:\/\/([^/?#]+)(?:[/?#]|$)/i);
      const domain = matches && matches[1];

      return domain ?? undefined;
    };

    const getIframeMatchingDomain = (src: string): HTMLIFrameElement[] => {
      const domain = getDomain(src);

      if (domain === undefined) {
        return [];
      }

      const iframes = document.querySelectorAll('iframe');
      const matchingIframes: HTMLIFrameElement[] = [];

      for (const iframe of iframes) {
        if (iframe.src.includes('/campaign/') && !iframe.src.includes('/campaign/view/demo/')) {
          continue;
        }

        const iframeDomain = getDomain(iframe.src);

        if (iframeDomain === domain) {
          matchingIframes.push(iframe);
        }
      }

      return matchingIframes;
    };

    const iframeDomain = computed<string | undefined>(() => {
      if (props.model.state.settings === undefined) {
        return undefined;
      }

      return getDomain(props.model.state.settings.url);
    });

    const onMessage = (event: MessageEvent) => {
      if (props.model.state.settings === undefined) {
        return undefined;
      }

      try {
        const data = JSON.parse(event.data);

        if (data && data.action && data.type && data.type === 'leadfamly-campaign' && props.model.state.settings.url) {
          if (data.data && data.uri && !props.model.state.settings.url.includes(data.uri)) {
            let preventEventHandler = true;

            // Support embedded campaigns that has redirected away from the previous campaign.
            if (getDomain(data.uri) === iframeDomain.value && getIframeMatchingDomain(data.uri).length === 1) {
              preventEventHandler = false;
            }

            if (preventEventHandler) {
              return;
            }
          }

          if (data.action === 'resize' && props.model.state.settings.responsiveHeight) {
            iframeResponsiveHeight.value = data.data.height;
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {}
    };

    const height = computed<CSSProperties['height']>(() => {
      if (props.model.state.settings === undefined) {
        return;
      }

      if (props.model.state.settings?.responsiveHeight && iframeResponsiveHeight.value !== undefined) {
        return `${iframeResponsiveHeight.value}px`;
      }

      if (props.model.state.settings?.fixedHeight) {
        return `${props.model.state.settings.fixedHeight}px`;
      }

      return undefined;
    });

    const iframeStyling = computed<CSSProperties>(() => {
      return {
        ...(props.model.state.settings?.fixedWidth ? { width: `${props.model.state.settings.fixedWidth}px` } : {}),
        height: height.value
      };
    });

    const wrapperClassList = computed<ClassList>(() => {
      if (props.model.state.settings === undefined) {
        return {};
      }

      return {
        'iframe-element--full-width':
          props.model.state.settings.width !== IframeWidthType.FIXED &&
          props.model.state.settings.width !== IframeWidthType.CONTENT,
        'iframe-element--fixed-width': props.model.state.settings.width === IframeWidthType.FIXED,
        [`iframe-element--align-${props.model.state.settings.alignment}`]: !!props.model.state.settings.alignment
      };
    });

    onMounted(() => {
      if (typeof window !== 'undefined') {
        window.addEventListener('message', onMessage);
      }
    });

    onUnmounted(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('message', onMessage);
      }
    });

    return {
      iframeStyling,
      wrapperClassList
    };
  }
});
</script>

<style lang="scss">
.iframe-element {
  position: relative;

  &--align-left {
    margin-right: auto;
  }

  &--align-right {
    margin-left: auto;
  }

  &--align-center {
    margin-left: auto;
    margin-right: auto;
  }

  &__item {
    width: 100%;
    height: 100%;
    display: block;
    overflow: hidden;
    border: 0;
    left: 0;
    position: absolute;
    top: 0;

    &:focus {
      outline: none;
    }

    .iframe-element--show-scrollbars & {
      overflow: auto;
    }
  }

  &__item[seamless] {
    display: block;
  }
}
</style>
